import {
  isContentStateEmpty,
  isDraftContent,
  isRichContent,
  isRichContentEmpty,
} from '@wix/ricos';
import { useSettings } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';

import {
  useAppSettings,
  useBuilderState,
  useCanEditMember,
  useMember,
  useSite,
} from '../../../../contexts/widgets';
import {
  BuilderState,
  type MemberAbout,
  SettingsSection,
  SettingsTab,
} from '../../../../types';
import { useCascadingSettings } from '../../Settings/hooks';
import settingsParams from '../../settingsParams';
import stylesParams from '../../stylesParams';

const defaultNumbersVisibility = {
  showCommentsStats: false,
  showLikesReceivedStats: false,
  showTopCommentsStats: false,
};

export const useNumbersVisibility = () => {
  const settings = useSettings();
  const { isForumInstalled } = useSite();

  const showCommentsStats = settings.get(settingsParams.showCommentsStats);
  const showLikesReceivedStats = settings.get(
    settingsParams.showLikesReceivedStats,
  );
  const showTopCommentsStats = settings.get(
    settingsParams.showTopCommentsStats,
  );

  return isForumInstalled
    ? { showCommentsStats, showLikesReceivedStats, showTopCommentsStats }
    : defaultNumbersVisibility;
};

export const useDescriptionRenderer = () => {
  const cascadingSettings = useCascadingSettings();
  const { shouldRenderAbout } = useAboutRendered();
  const numbersVisibility = useNumbersVisibility();
  const showAboutSection = cascadingSettings.get(
    settingsParams.showAboutSection,
    stylesParams.showAboutSection,
  );

  if (!showAboutSection) {
    return { shouldRenderDescription: false };
  }

  const shouldShowNumbers = Object.values(numbersVisibility).some(Boolean);

  return {
    shouldRenderDescription: shouldShowNumbers || shouldRenderAbout,
  };
};

export const isAboutEmpty = (about: MemberAbout | null) => {
  if (isRichContent(about)) {
    return isRichContentEmpty(about);
  }

  if (isDraftContent(about)) {
    return isContentStateEmpty(about);
  }

  return true;
};

export const useAboutRendered = () => {
  const { about } = useMember();
  const { canEditMember } = useCanEditMember();
  const isAboutFilled = !isAboutEmpty(about);

  return { shouldRenderAbout: canEditMember || isAboutFilled };
};

export const useEditButtonVisibility = ({
  isEmptyState,
  isEditMode,
}: {
  isEmptyState: boolean;
  isEditMode: boolean;
}) => {
  const { isEditor, isPreview, isMobile } = useEnvironment();
  const { canEditMember } = useCanEditMember();
  const appSettings = useAppSettings();
  const builderState = useBuilderState();

  const allowEditMember = canEditMember && (!isPreview || !isMobile);

  const isSectionsAboutDefaultOpen =
    appSettings.tabOpened === SettingsTab.Sections &&
    appSettings.sectionOpened === SettingsSection.About;

  const isDesignButtonsTabOpen =
    appSettings.tabOpened === SettingsTab.Design &&
    appSettings.sectionOpened === SettingsSection.ButtonStyle;

  const showButtonsInEditor =
    isEditor &&
    (isSectionsAboutDefaultOpen ||
      isDesignButtonsTabOpen ||
      builderState === BuilderState.EditingAboutDescription);

  const showPlaceholderDescription =
    builderState === BuilderState.WithAboutDescription;

  const showEditAboutButton =
    (allowEditMember && !isEmptyState && !isEditMode) ||
    (showButtonsInEditor &&
      builderState !== BuilderState.EditingAboutDescription) ||
    showPlaceholderDescription;

  return {
    showButtonsInEditor,
    showEditAboutButton,
    showPlaceholderDescription,
  };
};
